import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import { trpc } from 'api'
import { useToast } from '@ui/toasts/useToastManager'
import usePayments from '@order/hooks/usePayments'
import { usePrice } from 'hooks/useCurrency'
import {
  createGlobemedAttempt,
  isDefiniteRejection,
} from 'features/payment/globemed/globemedAttempt'
import { fetchGlobemedMetadata } from 'features/payment/globemed/globemedMetadata'

interface Props {
  open: boolean
  close: () => void
  numericOrderIds: number[]
  amount: number
  paymentId: number
}

// Removing GlobeMed only raises what the customer pays, so no staff ID is asked
export default function GlobeMedRemoveModal({
  open,
  close,
  numericOrderIds,
  amount,
  paymentId,
}: Props) {
  const { t } = useTranslation(['payment'])
  const text = 'payment:globemed.remove'

  const toast = useToast()
  const utils = trpc.useUtils()
  const setGlobemedAttempt = usePayments((s) => s.setGlobemedAttempt)
  const price = usePrice(amount)

  // ERP prepaid amount right before the request, to tell later if it landed
  const baselinePrepaid = useRef<number | null>(null)
  const [isPreparing, setIsPreparing] = useState(false)

  const settle = (status: 'removed' | 'uncertain') => {
    setGlobemedAttempt(
      numericOrderIds,
      createGlobemedAttempt({
        amount,
        paymentId,
        status,
        operation: 'remove',
        baselinePrepaid: baselinePrepaid.current,
      })
    )
  }

  const remove = trpc.order.payments.globemedRemove.useMutation({
    onSuccess: async () => {
      settle('removed')
      await utils.order.payments.metadata.invalidate()
      toast({ variant: 'success', text: t(`${text}.toast.success`) })
      handleClose()
    },
    onError: async (error) => {
      if (isDefiniteRejection(error.data?.code)) {
        toast({ variant: 'error', text: t(`${text}.toast.error`) })
        return
      }
      // The removal may still have reached ERP; resolve it from metadata
      settle('uncertain')
      await utils.order.payments.metadata.invalidate()
      toast({ variant: 'error', text: t(`${text}.toast.uncertain`) })
      handleClose()
    },
  })

  const isPending = isPreparing || remove.isPending

  const handleRemove = async () => {
    if (isPending) return
    setIsPreparing(true)
    const before = await fetchGlobemedMetadata(numericOrderIds)
    baselinePrepaid.current = before?.prepaid_amount ?? null
    setIsPreparing(false)
    remove.mutate({ payment_id: paymentId })
  }

  const handleClose = () => {
    baselinePrepaid.current = null
    remove.reset()
    close()
  }

  const handleUserClose = () => {
    if (isPending) return
    handleClose()
  }

  return (
    <ConfirmationModal
      open={open}
      onClose={handleUserClose}
      onConfirm={handleRemove}
      isPending={isPending}
      title={t(`${text}.title`)}
      desc={t(`${text}.desc`, { amount: price })}
      confirmLabel={t(`${text}.cta.remove`)}
      cancelLabel={t(`${text}.cta.cancel`)}
    />
  )
}
